import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Eye, EyeOff } from "lucide-react";
import AdminLayout from "@/pages/admin/AdminLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import api, { formatApiErrorDetail } from "@/lib/api";

const empty = {
  title: "", excerpt: "", content: "", category: "Recovery",
  cover_image: "https://images.unsplash.com/photo-1470071459604-3b5ec3a7fe05?auto=compress&w=1200&q=80",
  author: "New Beginnings Team", read_minutes: 5, published: true,
};

const AdminBlog = () => {
  const [posts, setPosts] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/blog").then((r) => setPosts(r.data));
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (p) => { setEditing(p); setForm({ ...p }); setOpen(true); };
  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    if (!form.title.trim() || !form.content.trim()) { toast.error("Title and content are required"); return; }
    setSaving(true);
    try {
      const payload = { ...form, read_minutes: Number(form.read_minutes) || 5 };
      if (editing) await api.put(`/blog/${editing.id}`, payload);
      else await api.post("/blog", payload);
      toast.success(editing ? "Article updated" : "Article published");
      setOpen(false);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    await api.delete(`/blog/${id}`);
    toast.success("Article deleted");
    load();
  };

  return (
    <AdminLayout>
      <div data-testid="admin-blog-page">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-serif text-4xl text-forest">Resources</h1>
            <p className="text-ink2 mt-1">Publish SEO articles that bring people to your door.</p>
          </div>
          <Button onClick={openNew} data-testid="blog-new" className="rounded-full bg-forest hover:bg-forestdark text-sand"><Plus className="w-4 h-4 mr-1" /> New article</Button>
        </div>

        <div className="grid md:grid-cols-2 gap-4">
          {posts.map((p) => (
            <div key={p.id} data-testid={`admin-blog-${p.id}`} className="rounded-2xl bg-white border border-line p-5 flex gap-4">
              <img src={p.cover_image} alt="" className="w-24 h-24 rounded-xl object-cover shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <Badge className="bg-sage/25 text-forest border-0">{p.category}</Badge>
                  {p.published
                    ? <span className="flex items-center gap-1 text-xs text-forest"><Eye className="w-3 h-3" /> Live</span>
                    : <span className="flex items-center gap-1 text-xs text-ink2"><EyeOff className="w-3 h-3" /> Draft</span>}
                </div>
                <h3 className="font-serif text-xl text-forest leading-tight line-clamp-2">{p.title}</h3>
                <p className="text-sm text-ink2 line-clamp-1 mt-1">{p.excerpt}</p>
                <div className="flex gap-2 mt-3">
                  <button onClick={() => openEdit(p)} data-testid={`blog-edit-${p.id}`} className="flex items-center gap-1 text-sm text-forest hover:text-terracotta"><Pencil className="w-3.5 h-3.5" /> Edit</button>
                  <button onClick={() => remove(p.id)} data-testid={`blog-delete-${p.id}`} className="flex items-center gap-1 text-sm text-ink2 hover:text-destructive"><Trash2 className="w-3.5 h-3.5" /> Delete</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-serif text-2xl text-forest">{editing ? "Edit article" : "New article"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Title</Label>
              <Input data-testid="blog-form-title" value={form.title} onChange={(e) => update("title", e.target.value)} className="border-line" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2"><Label>Category</Label><Input value={form.category} onChange={(e) => update("category", e.target.value)} className="border-line" /></div>
              <div className="space-y-2"><Label>Read minutes</Label><Input type="number" value={form.read_minutes} onChange={(e) => update("read_minutes", e.target.value)} className="border-line" /></div>
            </div>
            <div className="space-y-2"><Label>Cover image URL</Label><Input value={form.cover_image} onChange={(e) => update("cover_image", e.target.value)} className="border-line" /></div>
            <div className="space-y-2"><Label>Excerpt</Label><Textarea data-testid="blog-form-excerpt" value={form.excerpt} onChange={(e) => update("excerpt", e.target.value)} rows={2} className="border-line resize-none" /></div>
            <div className="space-y-2"><Label>Content (separate paragraphs with a blank line)</Label><Textarea data-testid="blog-form-content" value={form.content} onChange={(e) => update("content", e.target.value)} rows={8} className="border-line resize-none" /></div>
            <div className="flex items-center gap-3"><Switch checked={form.published} onCheckedChange={(v) => update("published", v)} data-testid="blog-form-published" /><Label>Published (visible on site)</Label></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="rounded-full border-line">Cancel</Button>
            <Button onClick={save} disabled={saving} data-testid="blog-form-save" className="rounded-full bg-forest hover:bg-forestdark text-sand">{saving ? "Saving…" : editing ? "Save changes" : "Publish"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

export default AdminBlog;
